import styled, { css } from 'styled-components';
import colors from '../../helpers/colors';
import mediaQuery from '../../helpers/mediaQuery';

const borderColor = colors.neutral04;

type TableProps = {
  hasCornerHeadingGap?: boolean;
};

/**
 * A styled <table> element for rendering a styled table. For use with <TableCell> for providing cell styling.
 *
 * Note that the structure of tables must be:
 * @example
 * <Table>
 *   <thead>
 *    <tr><TableCell isHeaderCell /></tr>
 *   </thead>
 *   <tbody>
 *     <tr><TableCell /></tr>
 *   </tbody>
 * </Table>
 *
 * i.e. <thead> and <tbody> must be used (although thead is optional). Header cells may be used outside of the
 * header row if required.
 */
export const Table = styled.table<TableProps>`
  border-spacing: 0;
  width: 100%;
  table-layout: auto;

  ${mediaQuery.tabletPortrait`
    table-layout: fixed;
  `}

  /* Ideally we would collapse the borders, and then we could specify the border in one go, however
  it is not possible to use border radius when the borders are collapsed. */
  border-collapse: separate;
  empty-cells: hide;

  & td:not(:empty),
  & th:not(:empty) {
    border-top: 1px solid ${borderColor};
    border-left: 1px solid ${borderColor};
  }

  & td:last-child,
  & th:last-child {
    border-right: 1px solid ${borderColor};
  }

  & tbody tr:last-child td,
  & tbody tr:last-child th {
    border-bottom: 1px solid ${borderColor};
  }

  // Top-left rounded edge for:
  // - first cell of the heading row
  & thead tr:first-child th:first-child,
  // - cell after the empty 'corner gap' cell in the heading row
  & thead th:empty + th,
  // - the first cell of the first body row if there's no header row
  & tbody:only-child tr:first-child td:first-child,
  & tbody:only-child tr:first-child th:first-child {
    border-top-left-radius: ${({ theme }) => theme.tableRadius};
  }

  // - the first cell of the first body row if there's a header with a 'missing corner'
  // (Note that there's no way of performing this check using pure CSS, so we must rely
  // on the table prop to tell us whether this case applies)
  ${({ hasCornerHeadingGap, theme }) =>
    hasCornerHeadingGap &&
    css`
      & tbody:not(:only-child) tr:first-child td:first-child,
      & tbody:not(:only-child) tr:first-child th:first-child {
        border-top-left-radius: ${theme.tableRadius};
      }
    `}

  // Top-right rounded corner for:
  // The last item in the header row
  & thead tr:first-child th:last-child,
  // The last item in the first body row if there's no header row
  & tbody:only-child tr:first-child td:last-child,
  & tbody:only-child tr:first-child th:last-child {
    border-top-right-radius: ${({ theme }) => theme.tableRadius};
  }

  // Bottom-left rounded corner for:
  // The first cell of the last body row
  & tbody tr:last-child td:first-child,
  & tbody tr:last-child th:first-child {
    border-bottom-left-radius: ${({ theme }) => theme.tableRadius};
  }

  // Bottom-right rounded corner for:
  // The last cell of the last body row
  & tbody tr:last-child td:last-child,
  & tbody tr:last-child th:last-child {
    border-bottom-right-radius: ${({ theme }) => theme.tableRadius};
  }
`;
